import {HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import { catchError, throwError, switchMap } from 'rxjs';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { settingStore, profileStore } from '@core/store/';
import { TokenValidationService } from '@core/services/token-validation.service';
import { SecurityService } from '@core/services';

export const ApiRequestInterceptor: HttpInterceptorFn = (req, next) => {
  const setting = inject(settingStore);
  const profile = inject(profileStore);
  const tokenValidation = inject(TokenValidationService);
  const securityService = inject(SecurityService);
  const router = inject(Router);

  const token = profile.data.token.value();
  const tenantId = profile.data.activeTenantId();
  const condominiumId = profile.data.activeCondominiumId();

  // Si la URL es para refresh token, no intentar refrescar
  const isRefreshRequest = req.url.includes('/auth/refresh');

  // Validar y refrescar token si es necesario (excepto para requests de refresh)
  if (!isRefreshRequest && token && token !== '') {
    // Si el token está expirado, intentar refrescar
    if (!tokenValidation.isTokenValid()) {
      if (tokenValidation.isRefreshTokenValid()) {
        return tokenValidation.refreshTokenIfNeeded().pipe(
          switchMap(refreshed => {
            if (refreshed) {
              // Token refrescado exitosamente, reintent request con nuevo token
              const newToken = profile.data.token.value();
              let headers = req.headers
                .set('Accept-Language-Custom', setting.data.language())
                .set('X-Requested-With', 'XMLHttpRequest')
                .set('Accept', 'application/json')
                .set('Content-Type', 'application/json')
                .set('Authorization', `Bearer ${newToken}`);

              if (tenantId != null && tenantId != '') {
                headers = headers.set('X-Tenant-Id', tenantId);
              }
              if (condominiumId != null && condominiumId != '') {
                headers = headers.set('X-Condominium-Id', condominiumId);
              }

              const modifiedReq = req.clone({ headers });
              return next(modifiedReq);
            } else {
              // No se pudo refrescar, logout
              securityService.logout();
              return throwError(() => new Error('Token expirado'));
            }
          })
        );
      } else {
        // Refresh token también expirado, logout
        securityService.logout();
        return throwError(() => new Error('Sesión expirada'));
      }
    }

    // Token válido pero próximo a expirar, intentar refrescar en background
    if (tokenValidation.isTokenNearExpiration()) {
      tokenValidation.refreshTokenIfNeeded().subscribe();
    }
  }

  // Preparar headers base
  let headers = req.headers
    .set('Accept-Language-Custom', setting.data.language())
    .set('X-Requested-With', 'XMLHttpRequest')
    .set('Accept', 'application/json')
    .set('Content-Type', 'application/json');

  // Agregar token de autorización si existe
  if (token != null && token != '') {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  if (tenantId != null && tenantId != '') {
    headers = headers.set('X-Tenant-Id', tenantId);
  }
  if (condominiumId != null && condominiumId != '') {
    headers = headers.set('X-Condominium-Id',condominiumId);
  }

  const modifiedReq = req.clone({
    headers: headers
  });

  return next(modifiedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        securityService.logout();
      }

      return throwError(() => error);
    })
  );
};
